import { useCallback, useState } from 'react';

import { ManagementType } from '@ndvr/portfolio-api';

import { SectionNav } from '@/components/SectionNav';
import { useHashScrollSpy } from '@/hooks/useHashScrollSpy';
import { useHouseholdRoutePaths, useRequiredParams } from 'utils';
import { getOnboardEligibility } from 'views/portfolio-engine/all-accounts/onboarding-wizard/onboard-eligibility';
import { OnboardingWizardModal } from 'views/portfolio-engine/all-accounts/onboarding-wizard/OnboardingWizardModal';
import { useAccount, useListPortfolioEngineAccountsForHousehold } from 'views/portfolio/account-queries';
import { PageSpinner } from 'widgets';

import { BoostBanner } from './BoostBanner';
import { BoostedInvestmentPolicy } from './BoostedInvestmentPolicy';
import { PolicyFeatureList, policySectionNames } from './PolicyFeatureList';

export const AccountInvestmentPolicyTab = () => {
    const { globalAccountGUID, accountGroupGUID, accountGUID } = useRequiredParams([
        'globalAccountGUID',
        'accountGroupGUID',
        'accountGUID',
    ]);
    const { PortfolioPageRoutePath } = useHouseholdRoutePaths();
    const [enrollmentAccountGUID, setEnrollmentAccountGUID] = useState<string>();
    const { data: account, isPending } = useAccount(accountGUID, { throwOnError: false });
    const {
        data: engineAccounts = [],
        isPending: isEnginePending,
        isError: isEngineError,
    } = useListPortfolioEngineAccountsForHousehold(globalAccountGUID, undefined, { throwOnError: false });
    const engineAccount = engineAccounts.find((item) => item.accountGUID === accountGUID);
    const managementType = (!isEngineError ? engineAccount?.managementType : undefined) ?? account?.managementType;
    const enrollmentPath = `${PortfolioPageRoutePath(globalAccountGUID, accountGroupGUID)}/overview`;
    const { canOnboard, onboardDisabledReason } = getOnboardEligibility(engineAccount ? [engineAccount] : []);
    const enrollmentDisabledReason = isEnginePending
        ? 'Checking Portfolio Boost enrollment eligibility…'
        : isEngineError
          ? 'Unable to check Portfolio Boost enrollment eligibility.'
          : canOnboard
            ? null
            : (onboardDisabledReason ?? 'Portfolio Boost enrollment is unavailable for this account.');
    const onEnroll = () => {
        if (!enrollmentDisabledReason) setEnrollmentAccountGUID(accountGUID);
    };
    const [dismissedAccountGUID, setDismissedAccountGUID] = useState<string>();
    const [scrollRoot, setScrollRoot] = useState<HTMLElement | null>(null);
    const contentRef = useCallback((element: HTMLDivElement | null) => {
        setScrollRoot(element?.closest<HTMLElement>('[role="tabpanel"]') ?? null);
    }, []);
    const { activeSection, sectionRef, endRef, scrollToSection } = useHashScrollSpy(policySectionNames, scrollRoot);

    if (!account && isPending) return <PageSpinner />;
    if (!account || managementType === undefined) {
        return <p className="text-cocoa-600 py-8">Investment policy information is currently unavailable.</p>;
    }
    if (managementType === ManagementType.AUTOMATIC) {
        return <BoostedInvestmentPolicy key={accountGUID} />;
    }

    return (
        <div ref={contentRef} className="flex flex-col gap-6 pt-6 lg:flex-row lg:gap-14">
            <SectionNav
                sections={policySectionNames}
                activeSection={activeSection}
                onSelect={scrollToSection}
                ariaLabel="Investment policy sections"
            />
            <div className="min-w-0 flex-1 lg:max-w-[1240px]">
                <BoostBanner
                    dismissed={dismissedAccountGUID === accountGUID}
                    sectionRef={sectionRef('Boost')}
                    enrollmentPath={enrollmentPath}
                    onEnroll={onEnroll}
                    disabledReason={enrollmentDisabledReason}
                    onDismiss={() => setDismissedAccountGUID(accountGUID)}
                    onRestore={() => setDismissedAccountGUID(undefined)}
                />
                <PolicyFeatureList sectionRef={sectionRef} />
                <div ref={endRef} aria-hidden="true" className="mt-12 h-px" />
            </div>
            {enrollmentAccountGUID === accountGUID && engineAccount && !enrollmentDisabledReason && (
                <OnboardingWizardModal accounts={[engineAccount]} onClose={() => setEnrollmentAccountGUID(undefined)} />
            )}
        </div>
    );
};
